import React, { ChangeEvent, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Collapse,
} from '@mui/material';
import clsx from 'clsx';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  CarBrandList,
  CarModelList,
  CarSubTypeList,
  CarType,
  CarTypeList,
} from 'entities/car';
import { apiClient } from 'shared/api';
import { useUser } from 'shared/api/tanstack/queries';
import CloseIcon from 'shared/assets/icons/close.svg';
import { useFormAutoSave } from 'shared/hooks/useFormAutoSave';
import { validateINN, ValidationRegular } from 'shared/model/constants/validations';
import { ButtonCustom } from 'shared/ui/button-custom';
import { CheckboxInput } from 'shared/ui/checkbox-input';
import { FileInputCustom } from 'shared/ui/file-input-custom';
import { PhoneInput } from 'shared/ui/phone-input';
import { SelectInput } from 'shared/ui/select-input';
import { TextInput } from 'shared/ui/text-input';
import styles from './main-selection-calc.module.scss';

interface SelectionCalcProps {
  isDashboard?: boolean;
  title: string;
  typeCars?: CarType;
}

export const MainSelectionCalc = (props: SelectionCalcProps) => {
  const { title, typeCars, isDashboard } = props;
  const [searchParams] = useSearchParams();

  const [open, setOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalDesc, setModalDesc] = useState('');
  const [titleModalBtn, setTitleModalBtn] = useState<string>('Войти в личный кабинет');
  const [url, setUrl] = useState<string>('/auth');

  const handleClose = () => {
    setOpen(false);
  };

  const [inn, setInn] = useState<string | number>('');
  const [bio, setBio] = useState<string | number>('');
  const [email, setEmail] = useState<string | number>('');
  const [phone, setPhone] = useState<string | undefined>('');
  const [vehicleType, setVehicleType] = useState<string | number>('');

  const [hasRequiredFields, setHasRequiredFields] = useState(false);

  useEffect(() => {
    setHasRequiredFields(!!inn && !!phone && !!email && !!bio);
  }, [inn, phone, email, bio]);

  const [quantity, setQuantity] = useState<string | number>('');

  const [bioErr, setBioErr] = useState(false);
  const [innErr, setInnErr] = useState(false);
  const [conditionErr, setConditionErr] = useState(false);
  const [emailErr, setEmailErr] = useState(false);
  const [phoneErr, setPhoneErr] = useState(false);
  const [quantityErr, setQuantityErr] = useState(false);
  const [car_brandErr, setCarBrandErr] = useState(false);
  const [car_modelErr, setCarModelErr] = useState(false);
  const [destination, setDestination] = useState<string>('');
  const [destinationErr, setDestinationErr] = useState(false);
  const [currency, setCurrency] = useState<string>('');
  const [currencyErr, setCurrencyErr] = useState(false);

  const [car_type, setCar_type] = useState<number | string>('');
  const [car_typeErr, setCarTypeErr] = useState(false);
  const [carTypeOtherBlock, showCarTypeOtherBlock] = useState(false);
  const [otherCarType, setOtherCarType] = useState('');
  const [carTypeOtherBlockErr, setCarTypeOtherBlockErr] = useState(false);
  const handleChangeOtherCarType = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherCarType(event.target.value);
  };
  const handleChangeCarType = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    const carType = typeValueList.find((item) => item.id === value);
    setCar_type(value as number);
    setBrand('');
    setModel('');
    setCar_subtype('');
    setCarTypeErr(false);
    setCarSubtypeErr(false);

    if (carType?.value === 'other') {
      showCarTypeOtherBlock(true);
    } else {
      setOtherCarType('');
      showCarTypeOtherBlock(false);
      getBrandsForType(value as number);
      getSubtypeForType(value as number);
    }
  };
  const CarTypeBlock = () => {
    return (
      <Box className={`${styles.ts} ${styles.small}`}>
        <SelectInput
          name={'car_type'}
          value={car_type}
          required
          error={car_typeErr}
          onChange={handleChangeCarType}
          placeholder={'Выберите тип ТС'}
        >
          {typeValueList?.map((item: { id: number; name: string; value: string }) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const [subTypeValueList, setSubTypeValueList] = useState<CarSubTypeList>([]);
  const [car_subtype, setCar_subtype] = useState<number | string>('');
  const [car_subtypeErr, setCarSubtypeErr] = useState(false);
  const [carSubtypeOtherBlock, showCarSubtypeOtherBlock] = useState(false);
  const [otherSubType, setOtherSubType] = useState('');
  const [carSubtypeOtherBlockErr, setCarSubtypeOtherBlockErr] = useState(false);
  const handleChangeOtherSubType = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherSubType(event.target.value);
  };
  const handleChangeCarSubtype = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    const carSubtype = subTypeValueList?.find((item) => item.id === value);

    setCar_subtype(value as number);
    setBrand('');
    setModel('');
    setCarSubtypeErr(false);

    if (carSubtype?.value === 'other') {
      showCarSubtypeOtherBlock(true);
    } else {
      setOtherSubType('');
      showCarSubtypeOtherBlock(false);
      getBrandsForSubType(value as number);
    }
  };
  const CarSubTypeBlock = () => {
    return (
      <Box className={`${styles.ts} ${styles.small}`}>
        <SelectInput
          value={car_subtype}
          name={'car_subtype'}
          onChange={handleChangeCarSubtype}
          placeholder={'Выберите подтип ТС'}
          disabled={conditionBrandDisable()}
          required
          error={car_subtypeErr}
        >
          {subTypeValueList.map((item: { id: number; name: string }) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const [carBrandOtherBlock, showCarBrandOtherBlock] = useState(false);
  const [otherBrand, setOtherBrand] = useState('');
  const [carBrandOtherBlockErr, setCarBrandOtherBlockErr] = useState(false);
  const handleChangeOtherBrand = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherBrand(event.target.value);
  };
  const handleChangeBrand = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value;
    const brand = brandValueList.find((item) => item.id === value);
    setBrand(event.target.value as number);
    setModel('');
    setCarBrandErr(false);

    if (brand?.value === 'other') {
      showCarBrandOtherBlock(true);
    } else {
      setOtherBrand('');
      showCarBrandOtherBlock(false);
      getVehicleForBrandAndSubType(value as number, car_subtype as number);
    }

    // if (!typeCars && !isDashboard) {
    //   getVehicleForBrand(event.target.value as number, car_type as number);
    // } else {
    //   getVehicleForBrandAndSubType(event.target.value as number, car_subtype as number);
    // }
  };
  const CarBrandOtherBlock = () => {
    return (
      <Box className={`${styles.brand} ${styles.small}`}>
        <SelectInput
          disabled={conditionBrandDisable()}
          value={brand}
          required
          error={car_brandErr}
          onChange={handleChangeBrand}
          placeholder={'Выберите марку'}
        >
          {brandValueList.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const [carModelOtherBlock, showCarModelOtherBlock] = useState(false);
  const [otherModel, setOtherModel] = useState('');
  const [carModelOtherBlockErr, setCarModelOtherBlockErr] = useState(false);
  const handleChangeOtherModel = (event: ChangeEvent<HTMLInputElement>) => {
    setOtherModel(event.target.value);
  };
  const handleChangeModel = (event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as number;
    const model = modelValueList.find((item) => item.id === value);
    setModel(value);
    setCarModelErr(false);

    if (model?.value === 'other') {
      showCarModelOtherBlock(true);
    } else {
      setOtherModel('');
      showCarModelOtherBlock(false);

      getVehicleParams(value);
      getVehicleSubtype(value);
      getVehicleType(value);
    }

    resetAllValues();
  };
  const CarModelOtherBlock = () => {
    return (
      <Box className={`${styles.payment} ${styles.big}`}>
        <SelectInput
          disabled={!brand}
          value={model}
          required
          error={car_modelErr}
          onChange={handleChangeModel}
          placeholder={'Выберите модель'}
        >
          {modelValueList.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.name}
            </MenuItem>
          ))}
        </SelectInput>
      </Box>
    );
  };

  const [check, setCheck] = useState(false);
  const [checkErr, setCheckErr] = useState(false);
  const handleChangeCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCheck(e.target.checked);
    e.target.checked ? setCheckErr(false) : setCheckErr(true);
  };
  const rules = (event: any) => {
    event?.preventDefault();
    const link = document.createElement('a');
    link.href = '/Политика_в_отношении_обработки_персональных_данных.pdf';
    link.download = 'Политика в отношении обработки персональных данных.pdf';
    link.click();
  };

  const handleChangeInn = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInn(value || '');
    const validInn = validateINN(value);

    setInnErr(!validInn);
  };
  const handleChangeBio = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setBio(value);
    const bio_valid: boolean = !(
      value?.toString().length && ValidationRegular.BIO.test(value?.toString())
    );

    setBioErr(bio_valid);
  };
  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEmail(value);
    const email_valid: boolean = !(
      value?.toString().length && ValidationRegular.MAIL.test(value?.toString())
    );

    setEmailErr(email_valid);
  };
  const handleChangePhone = (value: string) => {
    let formatted = value.replace(/[^\d+]/g, '');
    if (formatted.startsWith('8')) {
      formatted = '+7' + formatted.slice(1);
    }
    if (!formatted.startsWith('+7')) {
      formatted = '+7';
    }
    const digitsOnly = formatted.replace(/\D/g, '');
    if (digitsOnly.length > 11) {
      formatted = '+' + digitsOnly.slice(0, 11);
    }
    setPhone(formatted);
    const isValid = /^\+7\d{10}$/.test(formatted);
    setPhoneErr(!isValid);
  };
  const handleChangeQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuantity(value);
    const quantity_valid: boolean = !(
      value?.toString().length && ValidationRegular.QUANTITY.test(value?.toString())
    );

    setQuantityErr(quantity_valid);
  };

  const handleChangeDestination = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setDestination(value);
    setDestinationErr(!value?.trim());
  };

  const handleChangeCurrency = (event: SelectChangeEvent<unknown>) => {
    setCurrency(event.target.value as string);
    setCurrencyErr(false);
  };

  const [paymentVariantErr, setPaymentVariantErr] = useState(false);
  const [loading, setLoading] = useState(false);

  // TODO
  async function handleClickOpen() {
    const formData = new FormData();

    let carParamsArray = [];

    if (!check || checkErr) {
      setCheckErr(true);
    }

    if (selectedValues?.length) {
      carParamsArray = Object.entries(selectedValues).map(([key, value]) => ({
        fieldName: key,
        fieldValue: value,
      }));

      formData.append('car_params', JSON.stringify(carParamsArray));
    } else {
      carParamsArray = vehicleParamsMainList[0]?.car_params ?? [];
      formData.append('car_params', JSON.stringify(carParamsArray));
    }

    // Добавляем параметр fulfilled для обозначения завершенной заявки
    formData.append('fulfilled', 'true');

    if (user?.user_type === 'agent') {
      formData.append('agent_bio',  user?.bio ? user?.bio?.toString() : '-');
      formData.append('agent_email', user?.email ? user?.email?.toString() : '-');
      formData.append('agent_phone', user?.phone ? user?.phone?.toString() : '-');
      formData.append('agent_inn', user?.inn ? user?.inn?.toString() : '-');
      // formData.append('agent_reward', user?.reward?.toString());
    }

    if (email?.toString().length && ValidationRegular.MAIL.test(email?.toString())) {
      formData.append('email', email?.toString() || '');
    } else {
      setEmailErr(true);
    }

    if (phone?.toString().length) {
      formData.append('phone', phone?.toString() || '');
    } else {
      setPhoneErr(true);
    }
    if (bio?.toString().length && ValidationRegular.BIO.test(bio?.toString())) {
      formData.append('bio', bio?.toString() || '');
    } else {
      setBioErr(true);
    }

    if (validateINN(inn?.toString())) {
      formData.append('inn', inn?.toString() || '');
      setInnErr(false);
    } else {
      setInnErr(true);
    }

    if (quantity?.toString().length && ValidationRegular.QUANTITY.test(quantity?.toString())) {
      formData.append('quantity', quantity?.toString() || '');
    } else {
      setQuantityErr(true);
    }

    const conditions = Array.from(
      document.querySelectorAll<HTMLInputElement>('[name^="condition-"]:checked'),
    )
      .map((input) => (input.labels ? input.labels[0]?.innerText : ''))
      .filter((text) => text)
      .join(', ');

    if (conditions) {
      formData.append('condition', conditions);
      setConditionErr(false);
    } else {
      setConditionErr(true);
    }

    formData.append('type', 'select');

    if (paymentV.length > 2) {
      formData.append('pay_method', paymentV);
    } else {
      setPaymentVariantErr(true);
    }

    if (destination?.toString().trim()) {
      formData.append('destination', destination.toString().trim());
      setDestinationErr(false);
    } else {
      setDestinationErr(true);
    }

    if (currency?.toString().trim()) {
      formData.append('currency', currency.toString().trim());
      setCurrencyErr(false);
    } else {
      setCurrencyErr(true);
    }

    if (carTypeOtherBlock) {
      if (otherCarType) {
        formData.append('car_type_other', otherCarType);
        formData.append('car_subtype_other', '-');
        formData.append('car_brand_other', '-');
        formData.append('car_model_other', '-');

        setCarTypeOtherBlockErr(false);
        setCarTypeErr(false);
        setCarSubtypeErr(false);
        setCarBrandErr(false);
        setCarModelErr(false);
      } else {
        setCarTypeOtherBlockErr(true);
      }
    } else {
      if (vehicleType.toString()) {
        formData.append('car_type', vehicleType.toString());
      } else if (car_type) {
        formData.append('car_type', car_type.toString());
      } else {
        setCarTypeErr(true);
      }
    }
    if (carSubtypeOtherBlock) {
      if (otherSubType) {
        formData.append('car_subtype_other', otherSubType);
        formData.append('car_brand_other', '-');
        formData.append('car_model_other', '-');

        setCarSubtypeOtherBlockErr(false);
        setCarSubtypeErr(false);
        setCarBrandErr(false);
        setCarModelErr(false);
      } else {
        setCarSubtypeOtherBlockErr(true);
      }
    } else {
      if (car_subtype.toString()) {
        formData.append('car_subtype', car_subtype.toString());
      } else if (subtypeMain) {
        formData.append('car_subtype', subtypeMain.toString());
      } else {
        setCarSubtypeErr(true);
      }
    }

    if (carBrandOtherBlock) {
      if (otherBrand) {
        formData.append('car_brand_other', otherBrand);
        formData.append('car_model_other', '-');
        setCarBrandOtherBlockErr(false);
        setCarBrandErr(false);
        setCarModelErr(false);
      } else {
        setCarBrandOtherBlockErr(true);
      }
    } else {
      if (brand.toString()) {
        formData.append('car_brand', brand.toString());
      } else {
        setCarBrandErr(true);
      }
    }

    if (carModelOtherBlock) {
      if (otherModel) {
        formData.append('car_model_other', otherModel);

        setCarModelOtherBlockErr(false);
        setCarModelErr(false);
      } else {
        setCarModelOtherBlockErr(true);
      }
    } else {
      if (model.toString()) {
        formData.append('vehicle', model.toString());
      } else {
        setCarModelErr(true);
      }
    }

    if (kp && kp[0]) {
      formData.append('kp', kp[0]);
    }

    const referal = searchParams.get('referal');
    if (referal) {
      const response = await apiClient.get(`/agents/checkReferal?id=${referal}`);
      const data = response?.data?.data;

      if (!data?.exists) {
        console.log('Пользователь с таким реферальным кодом не найден');
      } else {
        formData.append('referal_id', referal);
      }
    }

    const typeError = carTypeOtherBlock ? carTypeOtherBlockErr : car_typeErr;
    const subtypeError =
      !carTypeOtherBlock && (carSubtypeOtherBlock ? carSubtypeOtherBlockErr : car_subtypeErr);
    const brandError =
      !carTypeOtherBlock &&
      !carSubtypeOtherBlock &&
      (carBrandOtherBlock ? carBrandOtherBlockErr : car_brandErr);
    const modelError =
      !carTypeOtherBlock &&
      !carSubtypeOtherBlock &&
      !carBrandOtherBlock &&
      (carModelOtherBlock ? carModelOtherBlockErr : car_modelErr);

    const valid = !!(
      !bioErr &&
      !emailErr &&
      !checkErr &&
      !phoneErr &&
      !quantityErr &&
      !innErr &&
      !paymentVariantErr &&
      !destinationErr &&
      !currencyErr &&
      paymentV?.length &&
      quantity &&
      destination?.toString().trim() &&
      currency?.toString().trim() &&
      !typeError &&
      !subtypeError &&
      !brandError &&
      !modelError
    );

    if (valid) {
      try {
        setLoading(true);

        const url =
          user?.user_type === 'client' || isDashboard
            ? '/select-applications/new'
            : '/new-user-app';

        await apiClient.post(url, formData);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        window.ym && window.ym?.(105273634, 'reachGoal', 'submit_selection_form_success');

        // Очищаем автосохраненные данные при успешной отправке
        clearAutoSavedData();

        if (isDashboard) {
          navigate(-1);
        } else {
          setModalTitle('Благодарим за оформление заявки!');
          setModalDesc(
            'На Ваш E-mail отправлено письмо, содержащее в себе логин и пароль для личного кабинета. В нем будут храниться все ваши заявки.',
          );
          setTitleModalBtn('Войти в личный кабинет');
          setUrl('/auth');
          setOpen(true);
        }
      } catch (error: any) {
        if (error.response?.data?.error?.errors) {
          // Карта для перевода полей на русский язык
          const fieldTranslations: Record<string, string> = {
            email: 'E-mail',
            phone: 'Телефон',
            inn: 'ИНН',
            bio: 'ФИО',
            quantity: 'Количество ТС',
            condition: 'Состояние',
            car_type: 'Тип ТС',
            car_subtype: 'Подтип ТС',
            car_brand: 'Марка',
            car_model: 'Модель',
            pay_method: 'Способ покупки',
            destination: 'Место поставки ТС',
            currency: 'Валюта',
          };
          const errorFields: string[] = [];
          const errorUniqueFields: string[] = [];
          error.response.data.error.errors.forEach((err: any) => {
            if (Array.isArray(err.path)) {
              err.path.forEach((field: string) => {
                switch (field) {
                  case 'email':
                    setEmailErr(true);
                    break;
                  case 'phone':
                    setPhoneErr(true);
                    break;
                  case 'inn':
                    setInnErr(true);
                    break;
                  case 'bio':
                    setBioErr(true);
                    break;
                  case 'quantity':
                    setQuantityErr(true);
                    break;
                  case 'condition':
                    setConditionErr(true);
                    break;
                  case 'car_type':
                    setCarTypeErr(true);
                    break;
                  case 'car_subtype':
                    setCarSubtypeErr(true);
                    break;
                  case 'car_brand':
                    setCarBrandErr(true);
                    break;
                  case 'car_model':
                    setCarModelErr(true);
                    break;
                  case 'pay_method':
                    setPaymentVariantErr(true);
                    break;
                  case 'destination':
                    setDestinationErr(true);
                    break;
                  case 'currency':
                    setCurrencyErr(true);
                    break;
                }
                const label = fieldTranslations[field] ? fieldTranslations[field] : field;

                if (err.message.includes('unique')) {
                  errorUniqueFields.push(label);
                } else {
                  errorFields.push(label);
                }
              });
            }
          });
          setModalTitle('Ошибка!');
          const uniqueText =
            errorUniqueFields.length > 0
              ? `Следующие поля должны быть уникальными: ${errorUniqueFields.join(', ')}.`
              : '';
          const errorText =
            errorFields.length > 0
              ? `Проверьте корректность заполнения следующих полей: ${errorFields.join(', ')}.`
              : '';
          const text = `${uniqueText} ${errorText}`.trim();
          if (text) {
            setModalDesc(text);
          } else {
            setModalDesc('Проверьте корректность заполнения полей.');
          }
          setTitleModalBtn('Продолжить');
          setUrl('');
        } else if (error.response?.data?.error?.message || error.response.data?.error) {
          const errorMessage = error.response.data?.error?.message || error.response.data?.error;
          if (errorMessage === 'User already exists') {
            setModalTitle('Ошибка! Вы уже зарегистрированы!');
            setModalDesc(
              'Создать новую или узнать статус текущих заявок можно в личном кабинете TLS Group.',
            );
            setTitleModalBtn('Войти в личный кабинет');
            setUrl('/auth');
          } else if (errorMessage === 'Missing required fields') {
            setModalTitle('Ошибка! Проверьте заполнение обязательных полей!');
            setModalDesc('Для оформления заявки должны быть заполнены все обязательные поля!');
            setTitleModalBtn('Продолжить');
            setUrl('');
          } else {
            setModalTitle('Ошибка!');
            setModalDesc('Произошла непредвиденная ошибка. Попробуйте снова позже.');
            setTitleModalBtn('Продолжить');
            setUrl('');
          }
        } else {
          setModalTitle('Ошибка!');
          setModalDesc('Произошла непредвиденная ошибка. Попробуйте снова позже.');
          setTitleModalBtn('Продолжить');
          setUrl('');
        }
        setOpen(true);
      } finally {
        setLoading(false);
      }
    }
  }

  const navigate = useNavigate();

  const [brandValueList, setBrandValueList] = useState<CarBrandList>([]);
  const [typeValueList, setTypeValueList] = useState<CarTypeList>([]);
  const [modelValueList, setModelValueList] = useState<CarModelList>([]);
  const [vehicleParamsMainList, setVehicleParamsMainList] = useState<any>([]);

  const [kp, setKp] = useState<any>();

  const getIdByName = (name: string | undefined, items: any) => {
    const item = items.find((item: any) => item.name === name);
    return item ? item.id : null;
  };
  const getNameById = (id: number | string, items: any) => {
    const item = items.find((item: any) => item.id === id);
    return item ? item.name : null;
  };

  const { data: user } = useUser();

  const userGetInfo = async () => {
    if (user?.user_type === 'client') {
      setBio(user.bio);
      setEmail(user.email);
      setPhone(user.phone);
      setInn(user.inn);
    }
  };

  // Функция для загрузки сохраненных данных
  const loadSavedData = () => {
    const savedData = getStoredData();
    if (savedData) {
      // eslint-disable-next-line no-console
      // console.log('Загружаем сохраненные данные формы:', savedData);

      if (savedData.bio) setBio(savedData.bio);
      if (savedData.email) setEmail(savedData.email);
      if (savedData.phone) setPhone(savedData.phone);
      if (savedData.inn) setInn(savedData.inn);
      if (savedData.quantity) setQuantity(savedData.quantity);
      if (savedData.pay_method) setPayment(savedData.pay_method);
      if (savedData.destination) setDestination(savedData.destination);
      if (savedData.currency) setCurrency(savedData.currency);

      // Загружаем автомобильные данные
      if (savedData.car_type && !savedData.car_type_other) {
        setCar_type(savedData.car_type);
      }
      if (savedData.car_type_other) {
        showCarTypeOtherBlock(true);
        setOtherCarType(savedData.car_type_other);
      }
      if (savedData.car_subtype && !savedData.car_subtype_other) {
        setCar_subtype(savedData.car_subtype);
      }
      if (savedData.car_subtype_other) {
        showCarSubtypeOtherBlock(true);
        setOtherSubType(savedData.car_subtype_other);
      }
      if (savedData.car_brand && !savedData.car_brand_other) {
        setBrand(savedData.car_brand);
      }
      if (savedData.car_brand_other) {
        showCarBrandOtherBlock(true);
        setOtherBrand(savedData.car_brand_other);
      }
      if (savedData.vehicle && !savedData.car_model_other) {
        setModel(savedData.vehicle);
      }
      if (savedData.car_model_other) {
        showCarModelOtherBlock(true);
        setOtherModel(savedData.car_model_other);
      }

      // Загружаем параметры автомобиля
      if (savedData.car_params) {
        try {
          const params = JSON.parse(savedData.car_params);
          if (Array.isArray(params)) {
            const selectedVals: { [key: string]: string | number } = {};
            params.forEach((param: any) => {
              if (param.fieldName && param.fieldValue) {
                selectedVals[param.fieldName] = param.fieldValue;
              }
            });
            setSelectedValues(selectedVals);
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Ошибка при загрузке car_params:', e);
        }
      }
    }
  };

  // useEffect(() => {
  //   userGetInfo();
  // }, [user]);

  useEffect(() => {
    // Загружаем сохраненные данные только после того, как получили информацию о пользователе
    if (user?.id) {
      userGetInfo();
    } else {
      loadSavedData();
    }
  }, [user]);

  const fetchCarTypesData = async () => {
    try {
      const response1 = await apiClient.get('/car-types/all');
      const data1 = await response1;
      setTypeValueList([
        ...(data1.data.data as CarTypeList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);

      if (typeCars) {
        const typeId = getIdByName(typeCars, data1.data.data);
        if (typeId) {
          const response2 = await apiClient.post('/car-subtypes/subtypes', {
            typeId: typeId,
          });
          const data2 = await response2;
          setSubTypeValueList([
            ...(data2.data.data as CarSubTypeList),
            { id: 0, name: 'Иное', value: 'other' },
          ]);
        }
      }
    } catch (error) {
      setTypeValueList([{ id: 0, name: 'Иное', value: 'other' }]);
      // eslint-disable-next-line no-console
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  useEffect(() => {
    fetchCarTypesData();
  }, [typeCars]);

  const getBrandsForType = async (car_type: number | string) => {
    try {
      const response = await apiClient.post('/car-brands/type', { typeId: car_type });
      const data = await response;
      setBrandValueList([
        ...(data.data.data as CarBrandList),
        { id: 0, name: 'Иное', value: 'other' },
      ]);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Ошибка при загрузке данных:', error);
    }
  };

  const getSubtypeForType = async (car_type: number | string) => {
    const response = await apiClient.post('/car-subtypes/subtypes', {
      typeId: car_type,
    });
    const data = await response;
    setSubTypeValueList([
      ...(data.data.data as CarSubTypeList),
      { id: 0, name: 'Иное', value: 'other' },
    ]);
  };

  const getBrandsForSubType = async (car_subtype: number | string) => {
    const response = await apiClient.post('/car-brands/subtype', { subtypeId: car_subtype });
    const data = await response;
    setBrandValueList([
      ...(data.data.data as CarBrandList),
      { id: 0, name: 'Иное', value: 'other' },
    ]);
  };

  // const getVehicleForBrand = async (brand: number | string, type: number | string) => {
  //   const response = await apiClient.post('/vehicles/brandType', { brandId: brand, typeId: type });
  //   const data = await response;
  //   setModelValueList([
  //     ...(data.data.data as CarModelList),
  //     { id: 0, name: 'Иное', car_params: [], value: 'other' },
  //   ]);
  // };

  const getVehicleForBrandAndSubType = async (brand: number | string, subtype: number | string) => {
    const response = await apiClient.post('/vehicles/brandSubType', {
      brandId: brand,
      subtypeId: subtype,
    });
    const data = await response;
    setModelValueList([
      ...(data.data.data as CarModelList),
      { id: 0, name: 'Иное', car_params: [], value: 'other' },
    ]);
  };

  const getVehicleParams = async (model: number | string) => {
    const response = await apiClient.post('/vehicles/car_params', { modelId: model });
    const data = await response;
    setVehicleParamsMainList(data.data.data);
  };

  const getVehicleSubtype = async (model: number | string) => {
    const response = await apiClient.post('/vehicles/subtype', { modelId: model });
    const data = await response;
    setSubtypeMain(data.data.data);
  };

  const getVehicleType = async (model: number | string) => {
    const response = await apiClient.post('/vehicles/give-type', { modelId: model });
    const data = await response;
    setVehicleType(data.data.data);
  };
  const [subtypeMain, setSubtypeMain] = useState<number | string>('');
  const [brand, setBrand] = useState<number | string>('');
  const [model, setModel] = useState<number | string>('');

  const [paymentV, setPayment] = useState<string>('');

  const handleChangePayment = (event: SelectChangeEvent<unknown>) => {
    setPayment(event.target.value as string);
    setPaymentVariantErr(false);
  };
  const handleChangeKp = (event: any) => {
    setKp(event.target.files);
  };

  const conditionBrandDisable = () => {
    return !typeCars && !car_type && !car_subtype;
  };

  const car_paramsTemplate = (car_subtypeName: string) => {
    if (car_subtypeName == 'Бульдозер') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Рабочий вес, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Модель двигателя'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Мощность двигателя л.с.'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Автокран') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Грузоподъемность, т'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Длина стрелы, м'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Макс. грузовой момент kH x m'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Асфальтоукладчик') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Мощность двигателя, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Производительность т/ч'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Ширина укладки, мм'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Автогрейдер') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Масса, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Модель двигателя'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Каток') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Рабочая масса, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Мощность, кВт'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Колесный экскаватор') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Номинальная мощность, кВт'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Объем ковша, м3'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Эксплуатационная масса, кг'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Мини-экскаватор') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Общий вес, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Мощность двигателя, kW'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Объем ковша, м3'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Фронтальный погрузчик') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Рабочий вес, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Объем ковша, м3'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Грузоподъемность, кг'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Гусеничный экскаватор') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Номинальная мощность, кВт'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Объем ковша, м3'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Эксплуатационная масса, кг'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Карьерный самосвал') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Грузоподъемность, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Мощность двигателя, кВт'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Дорожная фреза') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Мощность двигателя, л.с.'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Ширина фрезерования, мм'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Глубина фрезерования, мм'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Экскаватор-погрузчик') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Эксплуатационная масса, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Мощность двигателя, л.с.'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Объем ковша, м3'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Мини-погрузчик') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Эксплуатационная масса, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Грузоподъемность, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Объем ковша, м3'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Шоссейный самосвал') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Грузоподъемность, кг'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Мощность двигателя л.с.'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Объем кузова, м3'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Тягач магистральный') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Мощность двигателя, л.с.'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Скорость движения, км/ч'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Коробка передач'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Колесная формула'} />
          </Box>
        </>
      );
    }
    if (car_subtypeName == 'Автобетоносмеситель') {
      return (
        <>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Объем цистерны, м3'} />
          </Box>
          <Box className={`${styles.brand} ${styles.small}`}>
            <SelectInput disabled placeholder={'Мощность двигателя л.с.'} />
          </Box>
        </>
      );
    }
  };

  const [selectedValues, setSelectedValues] = useState<{ [key: string]: string | number }>({});

  // Собираем все данные формы для автосохранения
  const formData = useMemo(() => {
    const conditions = Array.from(
      document.querySelectorAll<HTMLInputElement>('[name^="condition-"]:checked'),
    )
      .map((input) => (input.labels ? input.labels[0]?.innerText : ''))
      .filter((text) => text)
      .join(', ');

    return {
      bio,
      email,
      phone,
      inn,
      quantity,
      car_type: carTypeOtherBlock ? otherCarType : car_type,
      car_subtype: carSubtypeOtherBlock ? otherSubType : car_subtype,
      car_brand: carBrandOtherBlock ? otherBrand : brand,
      vehicle: carModelOtherBlock ? otherModel : model,
      car_type_other: carTypeOtherBlock ? otherCarType : undefined,
      car_subtype_other: carSubtypeOtherBlock ? otherSubType : undefined,
      car_brand_other: carBrandOtherBlock ? otherBrand : undefined,
      car_model_other: carModelOtherBlock ? otherModel : undefined,
      condition: conditions || undefined,
      pay_method: paymentV,
      destination: destination?.toString().trim() || undefined,
      currency: currency?.toString().trim() || undefined,
      car_params:
        selectedValues && Object.keys(selectedValues).length > 0
          ? JSON.stringify(
              Object.entries(selectedValues).map(([key, value]) => ({
                fieldName: key,
                fieldValue: value,
              })),
            )
          : undefined,
    };
  }, [
    bio,
    email,
    phone,
    inn,
    quantity,
    car_type,
    car_subtype,
    brand,
    model,
    carTypeOtherBlock,
    otherCarType,
    carSubtypeOtherBlock,
    otherSubType,
    carBrandOtherBlock,
    otherBrand,
    carModelOtherBlock,
    otherModel,
    paymentV,
    selectedValues,
    destination,
    currency,
  ]);

  // Используем хук автосохранения
  const { getStoredData, clearAutoSavedData } = useFormAutoSave({
    formId: 'main-selection-calc',
    data: formData,
    enabled: !!phone && phone.length > 10 && !phoneErr,
    saveInterval: 2000,
    formType: 'select',
  });

  const resetAllValues = () => {
    setSelectedValues((prevState) => {
      const resetState: { [key: string]: string } = {};
      for (const key in prevState) {
        resetState[key] = '';
      }
      return resetState;
    });
  };

  const handleChange = (fieldName: string, value: string | number) => {
    setSelectedValues((prevState) => ({
      ...prevState,
      [fieldName]: value,
    }));
  };

  const car_params = (model: string | number) => {
    const vehicleModelList: CarModelList = modelValueList;

    const vehicleModel = vehicleModelList.find((item) => item.id === model);

    return (
      <>
        {vehicleModel?.car_params?.map((item) => (
          <Box key={item.fieldName} className={`${styles.payment} ${styles.small}`}>
            <SelectInput
              value={selectedValues[item.fieldName] || ''}
              onChange={(event) => handleChange(item.fieldName, event.target.value as number)}
              placeholder={item.fieldName}
            >
              {item.fieldValue?.map((value, index) => (
                <MenuItem key={`${item.fieldName}-${index}`} value={value}>
                  {value}
                </MenuItem>
              ))}
            </SelectInput>
          </Box>
        ))}
      </>
    );
  };

  return (
    <Box component='form' className={styles.calcContainer}>
      <Box className={styles.title}>{title}</Box>
      <>
        <Box className={`${styles.tel} ${styles.big}`}>
          <PhoneInput
            onChange={handleChangePhone}
            value={phone || ''}
            error={phoneErr}
            name={'phone'}
            required
            placeholder={'Введите телефон'}
          />
        </Box>
        <Box className={`${styles.inn} ${styles.big}`}>
          <TextInput
            name={'inn'}
            type={'number'}
            error={innErr}
            onChange={handleChangeInn}
            value={inn || ''}
            placeholder={'введите ИНН покупателя/лизингополучателя'}
            required
          />
        </Box>
        <Box className={`${styles.email} ${styles.big}`}>
          <TextInput
            onChange={handleChangeEmail}
            value={email || ''}
            error={emailErr}
            name={'email'}
            type={'email'}
            required
            placeholder={'Введите E-mail'}
          />
        </Box>
        <Box className={`${styles.bio} ${styles.big}`}>
          <TextInput
            onChange={handleChangeBio}
            value={bio || ''}
            error={bioErr}
            name={'bio'}
            type={'text'}
            placeholder={'введите ФИО представителя покупателя/лизингополучателя'}
            required
          />
        </Box>
      </>

      <Collapse
        in={hasRequiredFields}
        timeout={600}
        unmountOnExit
        sx={{
          '& .MuiCollapse-wrapper': {
            width: '100%',
          },
          '& .MuiCollapse-wrapperInner': {
            width: '100%',
          },
        }}
      >
        <Box className={styles.calcContainer}>
          <Box className={`${styles.condition} ${styles.small}`}>
            {/* todo: проверить функционал */}
            <FormControlLabel
              sx={{
                margin: 0,
                '.MuiFormControlLabel-label': {
                  fontSize: '16px',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                },
              }}
              control={
                <CheckboxInput
                  defaultChecked={true}
                  name={'condition-new'}
                  className={clsx([conditionErr && `${styles.condition_err}`])}
                />
              }
              label='Новый'
            />
            <FormControlLabel
              sx={{
                margin: 0,
                '.MuiFormControlLabel-label': {
                  fontSize: '16px',
                  fontWeight: 500,
                  letterSpacing: '0.02em',
                },
              }}
              control={
                <CheckboxInput
                  name={'condition-old'}
                  className={clsx([conditionErr && `${styles.condition_err}`])}
                />
              }
              label='С пробегом'
            />
          </Box>

          <Box className={`${styles.payment} ${styles.big}`}>
            <SelectInput
              name={'paymentVariant'}
              value={paymentV}
              onChange={handleChangePayment}
              required
              placeholder={'Выберите способ покупки'}
              error={paymentVariantErr}
            >
              <MenuItem value='Собственные средства'>Собственные средства</MenuItem>
              <MenuItem value='Лизинг'>Лизинг</MenuItem>
            </SelectInput>
          </Box>

          <Box className={`${styles.payment} ${styles.big}`}>
              <TextInput
                name={'destination'}
                type={'text'}
                onChange={handleChangeDestination}
                value={destination || ''}
                placeholder={'Введите место поставки ТС'}
                required
                error={destinationErr}
              />
          </Box>

          <Box className={`${styles.payment} ${styles.big}`}>
            <SelectInput
              name={'currency'}
              value={currency}
              onChange={handleChangeCurrency}
              required
              placeholder={'Выберите валюту'}
              error={currencyErr}
            >
              <MenuItem value='Рубль'>Рубль</MenuItem>
              <MenuItem value='Доллар'>Доллар</MenuItem>
              <MenuItem value='Евро'>Евро</MenuItem>
              <MenuItem value='Юань'>Юань</MenuItem>
            </SelectInput>
          </Box>

          <Box className={`${styles.quantity} ${styles.small}`}>
            <TextInput
              onChange={handleChangeQuantity}
              value={quantity || ''}
              error={quantityErr}
              required
              name={'quantity'}
              type={'number'}
              placeholder={'Введите кол-во ТС'}
            />
          </Box>

          <CarTypeBlock />
          {carTypeOtherBlock ? (
            <Box className={`${styles.bio} ${styles.big}`}>
              <TextInput
                name={'otherCarType'}
                type={'text'}
                onChange={handleChangeOtherCarType}
                value={otherCarType || ''}
                placeholder={'Введите свой тип ТС'}
                required
                error={carTypeOtherBlockErr}
              />
            </Box>
          ) : (
            <>
              <CarSubTypeBlock />
              {carSubtypeOtherBlock ? (
                <Box className={`${styles.bio} ${styles.big}`}>
                  <TextInput
                    name={'otherSubType'}
                    type={'text'}
                    onChange={handleChangeOtherSubType}
                    value={otherSubType || ''}
                    placeholder={'Введите свой подтип ТС'}
                    required
                    error={carSubtypeOtherBlockErr}
                  />
                </Box>
              ) : (
                <>
                  <CarBrandOtherBlock />
                  {carBrandOtherBlock ? (
                    <Box className={`${styles.bio} ${styles.big}`}>
                      <TextInput
                        name={'otherBrand'}
                        type={'text'}
                        onChange={handleChangeOtherBrand}
                        value={otherBrand || ''}
                        placeholder={'Введите свою марку'}
                        required
                        error={carBrandOtherBlockErr}
                      />
                    </Box>
                  ) : (
                    <>
                      <CarModelOtherBlock />
                      {carModelOtherBlock && (
                        <Box className={`${styles.bio} ${styles.big}`}>
                          <TextInput
                            name={'otherModel'}
                            type={'text'}
                            onChange={handleChangeOtherModel}
                            value={otherModel || ''}
                            placeholder={'Введите свою модель'}
                            required
                            error={carModelOtherBlockErr}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Collapse>

      {model ? car_params(model) : car_paramsTemplate(getNameById(car_subtype, subTypeValueList))}

      <Box className={`${styles.accept} ${styles.big}`}>
        <FormControlLabel
          sx={{
            margin: 0,
            '.MuiFormControlLabel-label': {
              fontSize: '16px',
              fontWeight: 500,
              letterSpacing: '0.02em',
              color: '#96A8B8',
            },
          }}
          control={
            <CheckboxInput
              className={clsx([checkErr && `${styles.accept_err}`])}
              name='remember'
              checked={check}
              onChange={handleChangeCheck}
            />
          }
          label={
            <div className={styles.grayRules}>
              <span>Я согласен с</span>{' '}
              <span className={styles.blueRules} onClick={() => rules(event)}>
                политикой обработки
              </span>
              <span className={styles.blueRules} onClick={() => rules(event)}>
                персональных данных
              </span>
            </div>
          }
        />
      </Box>

      <Box className={`${styles.file} ${styles.big}`}>
        <FileInputCustom
          value={kp}
          onChange={handleChangeKp}
          text={'Прикрепить файл с имеющимся предложением'}
        />
      </Box>
      <Box className={`${styles.submitBtn} ${styles.big}`}>
        <ButtonCustom onClick={handleClickOpen}>
          {loading ? 'Подождите...' : 'Оформить заявку на подбор'}
        </ButtonCustom>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
        sx={{
          '.MuiDialog-paper': {
            padding: '30px 30px 50px',
            borderRadius: '10px',
            maxWidth: '503px',
          },
        }}
      >
        <DialogTitle className={styles.modalTitle} id='alert-dialog-title'>
          <span>{modalTitle}</span>
          <IconButton
            disableRipple
            aria-label='close'
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              fontSize: 18,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText className={styles.modalDescription} id='alert-dialog-description'>
            <span>{modalDesc}</span>
          </DialogContentText>
        </DialogContent>
        <DialogActions className={styles.modalButtonWrapper}>
          {!isDashboard && (
            <ButtonCustom
              className={styles.modalButton}
              onClick={() => {
                navigate(`${url}`);
                setOpen(false);
              }}
            >
              {titleModalBtn}
            </ButtonCustom>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
